// ============================================================
// MongoDBSharding.jsx
//
// A branded guide to Horizontal Scaling and Data Partitioning
// using MongoDB Sharding architecture.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Accent      → #00E0FF  (Electric Blue / Connectivity)
//   Text        → #FFFFFF  (White)
//   Terminal    → #010409  (High-Contrast Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button
} from "@mui/material";

/* MUI Icons */
import HubIcon from "@mui/icons-material/Hub";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SpeedIcon from "@mui/icons-material/Speed";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import AltRouteIcon from "@mui/icons-material/AltRoute";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 224, 255, 0.2)", // Blue Border
    green: "#00ED64",
    blue: "#00E0FF",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function TechCard({ icon, title, description, color = BRAND.blue }) {
    return (
        <Paper elevation={0} sx={{
            p: 4, background: BRAND.surface, border: `1px solid ${BRAND.border}`,
            borderRadius: 4, transition: "0.3s", height: "100%",
            "&:hover": { borderColor: color, transform: "translateY(-4px)" }
        }}>
            <Avatar sx={{ bgcolor: `${color}15`, color: color, mb: 2 }}>{icon}</Avatar>
            <Typography variant="h6" sx={{ color: BRAND.white, mb: 1, fontWeight: 700 }}>{title}</Typography>
            <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>{description}</Typography>
        </Paper>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBSharding() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.blue }} />}
                        label="Scalability Architecture · Module 15"
                        sx={{ bgcolor: "rgba(0,224,255,0.1)", color: BRAND.blue, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Horizontal <span style={{ color: BRAND.blue }}>Sharding</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        When a single server can no longer handle your data or traffic, it's time to shard.
                        <strong> Sharding</strong> is the process of storing data records across multiple machines
                        to support very large datasets and high throughput operations.
                    </Typography>
                </Box>

                {/* 2. VERTICAL VS HORIZONTAL SCALING */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <SpeedIcon sx={{ color: BRAND.blue, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Vertical vs. Horizontal Scaling</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TechCard
                                title="Vertical (Scale Up)" icon={<StorageIcon />}
                                description="Increasing the capacity of a single server (More RAM/CPU). Limitation: There is a hard ceiling on hardware capacity and it becomes exponentially expensive."
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TechCard
                                title="Horizontal (Scale Out)" icon={<HubIcon />}
                                description="Dividing the dataset and distributing it over multiple servers (Shards). This allows for virtually infinite growth by adding more commodity hardware."
                            />
                        </Grid>
                    </Grid>
                </Box>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. CLUSTER COMPONENTS */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <AccountTreeIcon sx={{ color: BRAND.blue, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The Sharded Cluster Anatomy</Typography>
                    </Box>



                    <Grid container spacing={4} sx={{ mt: 2 }}>
                        {[
                            { name: "Shards", desc: "Each shard is a replica set that contains a subset of the sharded data.", icon: <StorageIcon /> },
                            { name: "Mongos", desc: "The query router. It acts as an interface between the app and the cluster.", icon: <AltRouteIcon /> },
                            { name: "Config Servers", desc: "Store metadata and configuration settings for the entire cluster.", icon: <SettingsInputComponentIcon /> }
                        ].map((comp, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Stack direction="row" spacing={2}>
                                    <Avatar sx={{ bgcolor: "rgba(255,255,255,0.05)", color: BRAND.blue }}>{comp.icon}</Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{comp.name}</Typography>
                                        <Typography variant="body2" sx={{ color: BRAND.muted }}>{comp.desc}</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 4. THE SHARD KEY */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 224, 255, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>The Most Critical Choice: The Shard Key</Typography>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        The <strong>Shard Key</strong> is a field (or fields) that exists in every document in the collection.
                        MongoDB uses this key to distribute the collection's documents across shards.
                        A poorly chosen shard key can lead to <strong>"Jumbo Chunks"</strong> or <strong>"Hot Shards"</strong> (one server doing all the work).
                    </Typography>



                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, bgcolor: "rgba(0,237,100,0.05)", border: `1px solid ${BRAND.green}` }}>
                                <Typography variant="subtitle2" sx={{ color: BRAND.green }}>Hashed Sharding</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Ensures even data distribution across the cluster (Better for writes).</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, bgcolor: "rgba(0,224,255,0.05)", border: `1px solid ${BRAND.blue}` }}>
                                <Typography variant="subtitle2" sx={{ color: BRAND.blue }}>Ranged Sharding</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Groups documents with similar keys together (Better for range-based reads).</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #003B4A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <HubIcon style={{ fontSize: 40, color: BRAND.blue, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready for Global Scale?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Understanding sharding is the difference between a local app and a global platform.
                        Next, let's explore how to secure these distributed clusters.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.blue, color: BRAND.bg, fontWeight: 900 }}>Practice Sharding</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Architecture Docs</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
